'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import { FilePropTypes } from 'DesignData/propTypes/finder';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import UIAutosizedTextInput from 'UIComponents/input/UIAutosizedTextInput';
import * as FolderAssetTypes from 'DesignData/constants/FolderAssetTypes';
import { KEYS as Keys } from 'DesignData/enums/Keys';
import { EXTENSION as SERVERLESS_EXTENSION } from 'DesignData/lib/serverless/serverless';
import withFilenameValidation from 'DesignComponents/decorators/withFilenameValidation';
const FileTypeExtensions = Object.freeze({
  [FolderAssetTypes.CSS]: 'css',
  [FolderAssetTypes.JS]: 'js',
  [FolderAssetTypes.TEMPLATE]: 'html'
});

class FolderNavRenameInput extends PureComponent {
  constructor(...args) {
    super(...args);
    this.inputRef = /*#__PURE__*/createRef();

    this.getInputNode = () => {
      return this.inputRef.current;
    };

    this.focusInput = () => {
      const node = this.getInputNode();
      if (node) node.focus();
    };

    this.selectInput = () => {
      const node = this.getInputNode();
      if (node) node.select();
    };

    this.handleSubmit = evt => {
      const {
        error,
        initialValue,
        onCancel,
        onSubmit,
        showValidationNotification
      } = this.props;

      if (error) {
        showValidationNotification({
          onClose: () => this.focusInput()
        });
        return;
      }

      let newValue = evt.target.value.trim();

      if (!newValue) {
        onCancel();
        return;
      }

      if (this.fileTypeExtension) {
        newValue = `${newValue}.${this.fileTypeExtension}`;
      }

      if (newValue !== initialValue) {
        onSubmit(newValue);
      } else {
        onCancel();
      }
    };

    this.handleBlur = evt => {
      this.handleSubmit(evt);
    };

    this.handleClick = evt => {
      evt.stopPropagation();
    };

    this.handleKeyUp = evt => {
      const {
        onCancel
      } = this.props;

      switch (evt.keyCode) {
        case Keys.ESCAPE:
          {
            onCancel();
            break;
          }

        case Keys.ENTER:
          {
            this.handleSubmit(evt);
            break;
          }

        default:
          break;
      }
    };

    this.handleChange = evt => {
      const {
        onChange
      } = this.props;
      let {
        value
      } = evt.target;

      if (this.fileTypeExtension) {
        value = `${value}.${this.fileTypeExtension}`;
      }

      onChange(SyntheticEvent(value));
    };
  }

  componentDidMount() {
    this.selectInput();
  }

  get fileTypeExtension() {
    const {
      fileType,
      initialValue
    } = this.props;
    const CODED_FILE_EXTENSION_REGEX = /\.(html|css|js)$/i;
    const [codedFileExtension] = initialValue.match(CODED_FILE_EXTENSION_REGEX) || [];

    if (codedFileExtension) {
      return codedFileExtension.slice(1);
    }

    if (fileType === FolderAssetTypes.RAW_ASSET) {
      return initialValue.split('.').pop();
    }

    if (this.props.isServerlessFunctionFolder) {
      return SERVERLESS_EXTENSION;
    }

    return FileTypeExtensions[fileType];
  }

  render() {
    const {
      error,
      isConfirmPending,
      onKeyPress
    } = this.props;
    let {
      value
    } = this.props;

    if (this.fileTypeExtension) {
      const fileExtensionIndex = value.lastIndexOf(this.fileTypeExtension);
      const actualExtension = value.slice(fileExtensionIndex);
      value = value.replace(`.${actualExtension}`, '');
    }

    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIAutosizedTextInput, {
        "data-test-id": "finder-rename-input",
        disabled: isConfirmPending,
        inputClassName: "folder-nav-item--text-input",
        onBlur: this.handleBlur,
        onChange: this.handleChange,
        onClick: this.handleClick,
        onKeyPress: onKeyPress,
        onKeyUp: this.handleKeyUp,
        inputRef: this.inputRef,
        error: error,
        value: value
      }), this.fileTypeExtension && /*#__PURE__*/_jsx("span", {
        children: `.${this.fileTypeExtension}`
      })]
    });
  }

}

FolderNavRenameInput.propTypes = {
  error: PropTypes.bool.isRequired,
  fileType: FilePropTypes.fileType,
  initialValue: PropTypes.string.isRequired,
  isConfirmPending: PropTypes.bool.isRequired,
  isServerlessFunctionFolder: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  showValidationNotification: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired
};
FolderNavRenameInput.defaultProps = {
  error: false,
  isConfirmPending: false,
  onKeyPress: emptyFunction
};
export default withFilenameValidation()(FolderNavRenameInput);